import { defineQueries, defineQuery } from '@rocicorp/zero';
import type { ExpressionFactory, Schema } from '@rocicorp/zero';

import type { OpEntityTable } from '@/mock/types';

import type { ZeroContext } from './context';
import { zql } from './schema';
import { TABLES } from './tables';
import type { TableSpec } from './tables';

export function channelVisibility(userId: string): ExpressionFactory<'channel', Schema> {
  return ({ and, cmp, exists, or }) =>
    or(
      and(cmp('kind', 'public'), cmp('closed', false)),
      exists('members', (members) => members.where('userId', userId)),
    );
}

export function personalFileVisibility(userId: string): ExpressionFactory<'attachment', Schema> {
  return ({ cmp, or }) =>
    or(cmp('ownerKind', '!=', 'storage'), cmp('folderId', 'IS NOT', null), cmp('uploaderId', userId));
}

export const SHARED_JOURNAL_TABLES: readonly OpEntityTable[] = [
  'user', 'project', 'folder', 'milestone', 'label', 'taskType', 'task', 'checklistItem',
  'comment', 'taskLink', 'timeEntry', 'subscription', 'attachment',
];

export function journalVisibility(userId: string): ExpressionFactory<'activityEvent', Schema> {
  return ({ cmp, or }) =>
    or(cmp('actorId', userId), cmp('entityTable', 'IN', [...SHARED_JOURNAL_TABLES]));
}

function requiredColumn(spec: TableSpec, column: string | undefined): string {
  if (column === undefined) {
    throw new Error(`У таблицы «${spec.name}» не назван столбец видимости`);
  }
  return column;
}

function visibleRows(spec: TableSpec, ctx: ZeroContext) {
  const rows = zql[spec.name];
  switch (spec.visibility) {
    case 'all':
      return rows;
    case 'own':
      return rows.where(requiredColumn(spec, spec.ownerColumn), ctx.userId);
    case 'journal':
      return rows.where(journalVisibility(ctx.userId));
    case 'personalFiles':
      return rows.where(personalFileVisibility(ctx.userId));
    case 'channel':
      return spec.name === 'channel'
        ? rows.where(channelVisibility(ctx.userId))
        : rows.whereExists('channel', (channel) => channel.where(channelVisibility(ctx.userId)));
  }
}

const syncQueries = Object.fromEntries(
  TABLES.map((spec) => [spec.name, defineQuery(({ ctx }) => visibleRows(spec, ctx))]),
);

export const queries = defineQueries({ sync: syncQueries });
